import { ExpiredIcon } from '@/assets/icons/expired';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { SEND_WELCOME_EMAIL_MUTATION } from '../queries';
import { IUser } from '../types';

interface SendWelcomeEmailProps {
  isOpen: boolean;
  currentUser: IUser | null;
  onClose: () => void;
}

const SendWelcomeEmail = ({ isOpen, currentUser, onClose }: SendWelcomeEmailProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [sendWelcomeEmail, { loading: sendWelcomeEmailLoading }] = useMutation(SEND_WELCOME_EMAIL_MUTATION);

  const onSave = () => {
    setErrorMessage(null);

    sendWelcomeEmail({
      variables: {
        data: {
          userId: currentUser?.id,
          tenantId: currentUser?.tenant?.id
        }
      },
      onCompleted: () => {
        onClose();
      },
      onError: () => {
        setErrorMessage(defaultErrorMessage);
      }
    });
  };

  if (!currentUser) return <></>;

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{
        name: 'Send Email',
        onClick: onSave,
        disabled: sendWelcomeEmailLoading
      }}
      showCloseIcon={!sendWelcomeEmailLoading}
      showCancelButton={!sendWelcomeEmailLoading}
      isUseOutsideClick={!sendWelcomeEmailLoading}
    >
      <ModalBody>
        <ExpiredIcon width={108} height={55} />
        <ModalTitle>Send Welcome Email</ModalTitle>
        {sendWelcomeEmailLoading ? (
          <ModalLoader />
        ) : (
          <>
            {errorMessage && (
              <ErrorBannerWrapper>
                <Banner
                  icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                  title="Error"
                  description={errorMessage}
                  bgColor={themeVar('context.error')}
                />
              </ErrorBannerWrapper>
            )}
            <ModalDescription>Send this new user instructions for logging in for the first time.</ModalDescription>
          </>
        )}
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default SendWelcomeEmail;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const ModalTitle = styled.div`
  font-family: Blinker;
  font-size: 33px;
  font-style: normal;
  font-weight: 400;
  line-height: 46.2px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  text-align: center;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  width: 100%;
  margin-bottom: 10px;
`;
